import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiCheckCircle, FiShield, FiTrendingUp, 
  FiHome, FiDollarSign, FiAlertTriangle, FiCheck, 
  FiInfo, FiThermometer, FiLayers, FiMapPin, FiHelpCircle,
  FiClock, FiX
} = FiIcons;

function Kerndaemmung() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": "Kerndämmung: Schnelle Wärmedämmung ohne Baustelle im Kreis Heinsberg & NRW",
        "description": "Kerndämmung (Hohlwanddämmung) für zweischaliges Mauerwerk. Vorteile, Ablauf, Kosten und Eignung für Häuser im Kreis Heinsberg, Niederrhein und Großraum Düsseldorf.",
        "author": {
          "@type": "Organization",
          "name": "MK-Ausbau",
          "url": "https://ausbau.nrw"
        },
        "publisher": {
          "@type": "Organization",
          "name": "MK-Ausbau",
          "logo": {
            "@type": "ImageObject",
            "url": "https://ausbau.nrw/logo-icon.svg"
          }
        },
        "datePublished": "2026-04-20",
        "image": "https://mx.ausbau.nrw/img/Daemmtechnik_xl.jpg",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://ausbau.nrw/blog/kerndaemmung"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Was ist eine Kerndämmung?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Bei der Kerndämmung wird der Hohlraum eines zweischaligen Mauerwerks mit Dämmstoff gefüllt, um Wärmeverluste über die Außenwand deutlich zu reduzieren."
            }
          },
          {
            "@type": "Question",
            "name": "Ist Kerndämmung im Altbau sinnvoll?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ja. Gerade Altbauten mit Klinkerfassade und Luftschicht profitieren stark von Kerndämmung, da sie meist völlig ungedämmt sind."
            }
          },
          {
            "@type": "Question",
            "name": "Wie lange hält eine Kerndämmung?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Bei fachgerechter Ausführung ist Kerndämmung dauerhaft und wartungsfrei."
            }
          },
          {
            "@type": "Question",
            "name": "Für welche Häuser ist Kerndämmung nicht geeignet?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Nicht geeignet sind einschalige Wände, stark durchfeuchtete Fassaden oder Gebäude ohne Hohlraum."
            }
          },
          {
            "@type": "Question",
            "name": "Was kostet eine Kerndämmung?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Die Kosten hängen von Wandfläche, Hohlraumtiefe und Dämmstoff ab. Sie liegen deutlich unter einer Außendämmung."
            }
          },
          {
            "@type": "Question",
            "name": "Wie lange dauert die Kerndämmung?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "In der Regel ein Arbeitstag bei Einfamilienhäusern."
            }
          },
          {
            "@type": "Question",
            "name": "Muss ich ausziehen?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Nein. Die Arbeiten erfolgen von außen."
            }
          },
          {
            "@type": "Question",
            "name": "Gibt es Förderungen?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ja, je nach Programm (z. B. BEG). Die Voraussetzungen ändern sich regelmäßig."
            }
          },
          {
            "@type": "Question",
            "name": "Kann Kerndämmung Schimmel verursachen?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Bei fachgerechter Planung nein. Voraussetzung ist eine trockene, geeignete Wand."
            }
          }
        ]
      }
    ]
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Kerndämmung Heinsberg & NRW: Hohlwanddämmung ohne Gerüst"
        description="Schnelle Wärmedämmung ohne Baustelle: Kerndämmung für zweischaliges Mauerwerk. Ideal für Altbauten in Heinsberg, Erkelenz & NRW. Jetzt informieren!"
        keywords="Kerndämmung Heinsberg, Hohlwanddämmung NRW, Einblasdämmung Kosten, Fassadendämmung ohne Gerüst, Zweischaliges Mauerwerk dämmen"
        schema={articleSchema}
        canonical="/blog/kerndaemmung"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <Link 
          to="/blog" 
          className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article {...fadeIn} className="prose prose-lg prose-mk max-w-none">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4 text-left">
              <SafeIcon icon={FiThermometer} />
              <span>Energie & Dämmung</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-6 text-left">
              Kerndämmung (Hohlwanddämmung): Schnelle Wärmedämmung ohne Baustelle
            </h1>
            <p className="text-xl font-medium text-mk-teal-700 mb-6">
              Ideal für Häuser im Kreis Heinsberg & NRW
            </p>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Experten-Ratgeber</span>
              <span className="flex items-center"><SafeIcon icon={FiInfo} className="mr-1" /> Stand: April 2026</span>
            </div>
          </header>

          {/* Intro */}
          <div className="lead text-xl text-gray-600 leading-relaxed mb-12 text-left">
            Viele Einfamilienhäuser im Kreis Heinsberg, am Niederrhein und im Großraum Düsseldorf haben ein enormes, oft ungenutztes Einsparpotenzial: <strong>eine ungedämmte Hohlwand.</strong>
          </div>

          <p className="text-gray-700">
            Die sogenannte <strong>Kerndämmung</strong> (auch Hohlwanddämmung oder Einblasdämmung) gehört zu den wirtschaftlichsten Dämmmaßnahmen überhaupt – ohne Gerüst, ohne große Baustelle und meist innerhalb eines Tages umgesetzt.
          </p>

          <p className="text-gray-700 mb-8">
            Gerade in Städten wie Heinsberg, Erkelenz, Hückelhoven, Geilenkirchen, Wegberg, Wassenberg, aber auch in Mönchengladbach, Viersen, Neuss und Düsseldorf, ist diese Bauweise weit verbreitet.
          </p>

          {/* Was ist Kerndämmung */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiLayers} className="mr-3 text-mk-teal-600" />
            Was ist eine Kerndämmung?
          </h2>
          <p className="text-gray-700 mb-4">
            Bei der Kerndämmung wird der Hohlraum zwischen zwei Mauerschalen (zweischaliges Mauerwerk) vollständig mit Dämmstoff gefüllt.
          </p>
          <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm mb-6">
            <h4 className="font-bold text-gray-900 mb-2">Typischer Wandaufbau:</h4>
            <ul className="space-y-2 list-none pl-0">
              <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2"/> Außenschale (Klinker)</li>
              <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2"/> Hohlraum (meist 4–8 cm)</li>
              <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2"/> Innenschale (tragendes Mauerwerk)</li>
            </ul>
          </div>
          <p className="text-gray-700">
            Durch kleine Bohrlöcher in der Fassade wird der Dämmstoff eingeblasen – die Fassade bleibt optisch nahezu unverändert.
          </p>

          {/* Warum sinnvoll */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Warum ist Kerndämmung im Raum Heinsberg so sinnvoll?</h2>
          <p className="text-gray-700 mb-6">
            Im Kreis Heinsberg und Niederrhein wurden viele Häuser zwischen 1950 und 1990 zweischalig gebaut – oft ohne jede Dämmung.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-red-50 p-6 rounded-xl">
              <h4 className="font-bold text-red-800 mb-2 flex items-center"><SafeIcon icon={FiAlertTriangle} className="mr-2"/> Das Problem</h4>
              <ul className="text-sm text-red-900 space-y-1">
                <li>• Hohe Wärmeverluste über die Außenwand</li>
                <li>• Kalte Innenwandoberflächen</li>
                <li>• Höherer Heizenergieverbrauch</li>
              </ul>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h4 className="font-bold text-green-800 mb-2 flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-2"/> Die Lösung</h4>
              <ul className="text-sm text-green-900 space-y-1">
                <li>• Bis zu 70–80 % Wandwärmeverluste reduzieren</li>
                <li>• Wohnkomfort spürbar erhöhen</li>
                <li>• Heizkosten deutlich senken</li>
              </ul>
            </div>
          </div>

          {/* Dämmstoffe */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Welche Dämmstoffe kommen zum Einsatz?</h2>
          <p className="text-gray-700 mb-6">
            Je nach Gebäude, Wandbreite und Feuchteschutz kommen unterschiedliche Materialien infrage:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
            <li><strong>Mineralische Dämmstoffe</strong> (z. B. Mineralwolle-Granulat)</li>
            <li><strong>Kunststoffbasierte Dämmstoffe</strong> (z. B. EPS-Perlen)</li>
            <li><strong>Naturbasierte Dämmstoffe</strong> (projektabhängig, z. B. Holzfaser-Granulate)</li>
          </ul>
          <p className="text-gray-700 italic bg-gray-50 p-4 rounded-lg border-l-4 border-mk-teal-500">
            <strong>Wichtig:</strong> Fachbetriebe wie MK-Ausbau prüfen vorab genau, welcher Dämmstoff technisch sinnvoll ist – nicht jeder Stoff passt zu jeder Wand. Faktoren sind Hohlraumtiefe, Zustand der Wand und Feuchtebelastung.
          </p>

          {/* Vorteile */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiTrendingUp} className="mr-3 text-mk-teal-600" />
            Vorteile der Kerndämmung auf einen Blick
          </h2>
          <div className="bg-mk-teal-50 p-8 rounded-2xl mb-12">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "Keine Großbaustelle",
                "Keine Veränderung der Fassade",
                "Meist 1-Tages-Maßnahme",
                "Sehr gutes Kosten-Nutzen-Verhältnis",
                "Sofort spürbare Wirkung",
                "Förderfähig (je nach Programm)"
              ].map((item, i) => (
                <li key={i} className="flex items-center text-mk-teal-900 font-medium">
                  <SafeIcon icon={FiCheckCircle} className="text-mk-teal-600 mr-3 w-5 h-5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600 mt-6 pt-4 border-t border-mk-teal-200">
              Gerade für Häuser in Gangelt, Dremmen, Übach-Palenberg oder Hückelhoven ist Kerndämmung oft der schnellste Einstieg in die energetische Sanierung.
            </p>
          </div>

          {/* Risiken */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Gibt es Risiken bei der Kerndämmung?</h2>
          <p className="text-gray-700 mb-4">
            Eine fachgerecht geplante Kerndämmung ist dauerhaft sicher – entscheidend sind jedoch:
          </p>
          <ul className="space-y-2 mb-6 text-gray-700">
            <li className="flex items-center"><SafeIcon icon={FiAlertTriangle} className="text-orange-500 mr-2"/> Prüfung auf Feuchtigkeit</li>
            <li className="flex items-center"><SafeIcon icon={FiAlertTriangle} className="text-orange-500 mr-2"/> Ausreichende Hohlraumtiefe</li>
            <li className="flex items-center"><SafeIcon icon={FiAlertTriangle} className="text-orange-500 mr-2"/> Saubere, vollständige Verfüllung</li>
          </ul>
          <p className="text-gray-700">
            Wird ohne Prüfung gedämmt, kann es zu Problemen kommen. <strong>Deshalb ist eine Vor-Ort-Analyse immer Teil eines seriösen Angebots.</strong>
          </p>

          {/* Vergleich */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Kerndämmung vs. Außendämmung – was ist besser?</h2>
          <div className="overflow-x-auto mb-8 rounded-xl border border-gray-200 shadow-sm">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-mk-teal-600 text-white">
                <tr>
                  <th className="px-6 py-4">Maßnahme</th>
                  <th className="px-6 py-4">Kosten</th>
                  <th className="px-6 py-4">Eingriff</th>
                  <th className="px-6 py-4">Wirkung</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="bg-white">
                  <td className="px-6 py-4 font-bold">Kerndämmung</td>
                  <td className="px-6 py-4 text-green-600 font-bold">niedrig</td>
                  <td className="px-6 py-4 text-green-600">minimal</td>
                  <td className="px-6 py-4 font-bold">sehr gut</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-bold">Außendämmung (WDVS)</td>
                  <td className="px-6 py-4 text-red-600">hoch</td>
                  <td className="px-6 py-4 text-red-600">stark</td>
                  <td className="px-6 py-4 font-bold">sehr gut</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="bg-gray-100 p-4 rounded-lg text-gray-800 text-sm font-bold border-l-4 border-gray-400">
            👉 Praxisregel: Wenn eine Kerndämmung möglich ist, sollte sie immer zuerst umgesetzt werden. Eine spätere Außendämmung bleibt trotzdem möglich.
          </div>

          {/* Eignung */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Für welche Häuser ist Kerndämmung geeignet?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <h3 className="text-green-700 font-bold mb-3 flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-2"/> Geeignet</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Einfamilienhäuser mit Klinkerfassade</li>
                <li>• Baujahre ca. 1950–1990</li>
                <li>• Zweischaliges Mauerwerk mit Luftschicht</li>
              </ul>
            </div>
            <div>
              <h3 className="text-red-700 font-bold mb-3 flex items-center"><SafeIcon icon={FiX} className="mr-2"/> Nicht geeignet</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Einschaliges Mauerwerk</li>
                <li>• Natursteinwände ohne Hohlraum</li>
                <li>• Stark durchfeuchtete Fassaden</li>
              </ul>
            </div>
          </div>

          {/* Städte */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiMapPin} className="mr-3 text-mk-teal-600" />
            Kerndämmung in diesen Städten (NRW)
          </h2>
          <p className="text-gray-700 mb-6">MK-Ausbau führt Kerndämmungen regelmäßig durch in:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
            <div>
              <h4 className="font-bold text-mk-teal-700 mb-3 border-b border-mk-teal-100 pb-2">Kreis Heinsberg</h4>
              <ul className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                <li>• Heinsberg</li>
                <li>• Erkelenz</li>
                <li>• Hückelhoven</li>
                <li>• Dremmen</li>
                <li>• Gangelt</li>
                <li>• Geilenkirchen</li>
                <li>• Übach-Palenberg</li>
                <li>• Wassenberg</li>
                <li>• Wegberg</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-mk-teal-700 mb-3 border-b border-mk-teal-100 pb-2">Niederrhein & Rheinland</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Mönchengladbach</li>
                <li>• Viersen</li>
                <li>• Neuss</li>
                <li>• Düsseldorf</li>
              </ul>
            </div>
          </div>

          {/* Fazit */}
          <div className="bg-mk-teal-600 text-white p-8 rounded-2xl shadow-xl mt-16 mb-12">
            <h2 className="text-white text-2xl font-bold mb-4 mt-0">Fazit: Kleine Maßnahme, große Wirkung</h2>
            <p className="text-mk-teal-50 mb-4">
              Die Kerndämmung gehört zu den effektivsten Dämmmaßnahmen im Bestand. Mit vergleichsweise geringem Aufwand lassen sich Heizkosten senken, Wohnkomfort steigern und Energieeffizienz verbessern.
            </p>
            <p className="text-white font-bold">
              Gerade im Kreis Heinsberg und NRW ist sie oft der beste erste Sanierungsschritt.
            </p>
          </div>

          {/* FAQ Section */}
          <div className="mt-20 pt-12 border-t border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              FAQ – Kerndämmung / Hohlwanddämmung
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "Was kostet eine Kerndämmung?",
                  a: "Die Kosten hängen von Wandfläche, Hohlraumtiefe und Dämmstoff ab. Sie liegen deutlich unter einer Außendämmung."
                },
                {
                  q: "Wie lange dauert die Kerndämmung?",
                  a: "In der Regel ein Arbeitstag bei Einfamilienhäusern."
                },
                {
                  q: "Muss ich ausziehen?",
                  a: "Nein. Die Arbeiten erfolgen von außen."
                },
                {
                  q: "Gibt es Förderungen?",
                  a: "Ja, je nach Programm (z. B. BEG). Die Voraussetzungen ändern sich regelmäßig."
                },
                {
                  q: "Kann Kerndämmung Schimmel verursachen?",
                  a: "Bei fachgerechter Planung nein. Voraussetzung ist eine trockene, geeignete Wand."
                }
              ].map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 hover:bg-mk-teal-50 transition-colors cursor-default">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start">
                    <SafeIcon icon={FiHelpCircle} className="text-mk-teal-500 mt-1 mr-3 flex-shrink-0" />
                    {faq.q}
                  </h3>
                  <p className="text-gray-700 text-sm ml-8 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center py-12 mt-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Ist Ihr Haus für Kerndämmung geeignet?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Wir prüfen Ihre Fassade und den Hohlraum vor Ort in Heinsberg und Umgebung. Unverbindlich und ehrlich.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/kontakt"
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Kerndämmung anfragen
              </Link>
              <Link
                to="/daemmtechnik"
                className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Mehr zur Dämmtechnik
              </Link>
            </div>
          </div>
        </motion.article>
      </div>

      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default Kerndaemmung;